import React, { forwardRef, useId, useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import styles from "./Input.module.css";

type InputProps = {
  className?: string;
  inputClassName?: string;
  width?: number;

  value?: string | number;
  defaultValue?: string;
  onChange?: (value: string) => void;

  error?: string | null;
  clearable?: boolean;
  /**
   * Renders a show/hide eye for `type="password"`. Opt-in rather than automatic so existing
   * password fields (user form, mail settings) keep their behavior until they choose it.
   */
  passwordToggle?: boolean;
  disabled?: boolean;
  onKeyDown?: React.KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  onEnter?: () => void;

  as?: "input" | "textarea";
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "defaultValue" | "className" | "width"
> &
  Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    "onChange" | "value" | "defaultValue" | "className" | "width"
  >;

export const Input = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputProps
>((props, ref) => {
  const {
    className,
    inputClassName,
    width,
    value,
    defaultValue,
    onChange,
    error,
    clearable,
    passwordToggle,
    disabled,
    onEnter,
    as = "input",
    onKeyDown,
    ...rest
  } = props;

  const errorId = useId();
  const isInvalid = Boolean(error);

  const handleKeyDown: React.KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    if (e.key === "Enter" && onEnter && as !== "textarea") {
      onEnter();
    }
    onKeyDown?.(e);
  };

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    onChange?.(e.target.value);
  };

  const showClear =
    clearable &&
    !disabled &&
    as !== "textarea" &&
    typeof value === "string" &&
    value.length > 0;

  const handleClear = () => {
    onChange?.("");
  };

  const [showPassword, setShowPassword] = useState(false);
  const hasToggle =
    passwordToggle && as !== "textarea" && rest.type === "password";
  // Padding is reserved from the props, not from visibility, so the text never
  // jumps when the ✕ appears on first keystroke.
  const trailingSlots =
    (clearable && as !== "textarea" ? 1 : 0) + (hasToggle ? 1 : 0);

  const isLocaleInput = ["date", "time", "datetime-local", "month", "week"].includes(
    rest.type ?? ""
  );

  const commonProps = {
    ref,
    className: [
      styles.input,
      as === "textarea" ? styles.textarea: "",
      inputClassName ?? "",
      disabled ? styles.disabled : "",
      isInvalid ? styles.errorBorder : "",
      trailingSlots === 1 ? styles.padTrailing1 : "",
      trailingSlots === 2 ? styles.padTrailing2 : "",
    ].join(" "),
    "aria-invalid": isInvalid,
    "aria-describedby": isInvalid ? errorId : undefined,
    disabled,
    onKeyDown: handleKeyDown,
    onChange: handleChange,
    value,
    defaultValue,
    lang: isLocaleInput ? "de" : undefined,
    ...rest,
    // After the spread — `rest` carries the caller's `type="password"`.
    type: hasToggle && showPassword ? "text" : rest.type,
  };

  return (
    <div
      className={`${styles.inputContainer} ${className ?? ""}`}
      style={{ width: width ? `${width}px` : undefined }}
    >
      {as === "textarea" ? (
        <textarea
          {...(commonProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <div className={styles.field}>
          <input
            {...(commonProps as React.InputHTMLAttributes<HTMLInputElement>)}
          />

          {showClear && (
            <button
              type="button"
              // Keeps focus (and the caret) in the input instead of blurring it.
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              className={`${styles.trailingButton} ${
                hasToggle ? styles.trailingButtonSecond : ""
              }`}
              aria-label="Eingabe löschen"
            >
              <X size={18} aria-hidden />
            </button>
          )}

          {hasToggle && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowPassword((v) => !v)}
              className={styles.trailingButton}
              aria-label={
                showPassword ? "Passwort verbergen" : "Passwort anzeigen"
              }
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <EyeOff size={18} aria-hidden />
              ) : (
                <Eye size={18} aria-hidden />
              )}
            </button>
          )}
        </div>
      )}

      {isInvalid && (
        <span id={errorId} className={styles.errorText} role="alert">
          {error}
        </span>
      )}
    </div>
  );
});

Input.displayName = "Input";